<template>
  <Dialog
    v-model:visible="visible"
    modal
    :header="`Create New ${fieldLabel}`"
    :style="{ width: '500px' }"
    @hide="onHide">
    <div class="space-y-4">
      <FormGroup label="Field Name" :error="fieldError">
        <InputText
          v-model="fieldName"
          class="w-full"
          :invalid="!!fieldError"
          placeholder="e.g., q_favorite_color"
          @input="validateField" />
        <small class="text-surface-500 dark:text-surface-400">
          Letters, numbers, and underscores only - no spaces or special characters
        </small>
      </FormGroup>

      <div v-if="segmentGroup" class="space-y-2">
        <div class="flex space-x-2.5">
          <ToggleSwitch v-model="autoGenerateMappings" />
          <div class="font-bold">
            {{ autoGenerateMappings ? 'Auto-generate mappings' : 'Set mappings manually' }}
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <Button label="Cancel" outlined @click="visible = false" />
      <Button
        label="Create"
        :disabled="!fieldName || !!fieldError || isCreating"
        :loading="isCreating"
        @click="handleCreate" />
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { ref, computed, watch, inject } from 'vue'
import type { Ref } from 'vue'
import { generateCombinationsForSegmentGroup } from '@dashboard/modules/segment/services/segmentUtilities'
import type { Dimension } from '@dashboard/types/config'
import FormGroup from '@dashboard/components/common/FormGroup.vue'
import { useIntegrationDataStore } from '@dashboard/modules/integration/stores/integrationDataStore'
import { useIntegrationStore } from '@dashboard/modules/integration/stores/integrationStore'
import { toSnakeCase } from '@dashboard/services/string'
import { useConfigStore } from '@dashboard/stores/configStore'
import { useNotificationStore } from '@dashboard/stores/notificationStore'
import { storeToRefs } from 'pinia'
import Button from 'primevue/button'
import Dialog from 'primevue/dialog'
import InputText from 'primevue/inputtext'
import ToggleSwitch from 'primevue/toggleswitch'

interface Props {
  modelValue: boolean
}

const props = defineProps<Props>()
const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  created: [fieldId: string]
}>()

const visible = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
})

const integrationStore = useIntegrationStore()
const integratedPartner = computed(() => integrationStore.integratedPartner)
const integrationDataStore = useIntegrationDataStore()
const configStore = useConfigStore()
const { currentTeamPid } = storeToRefs(configStore)
const notificationStore = useNotificationStore()

// Try to get segment group context if available
const segmentGroup = inject<Ref<Dimension> | null>('segmentGroup', null)

const fieldName = ref('')
const fieldError = ref('')
const autoGenerateMappings = ref(true)
const isCreating = ref(false)
const customFieldsData = ref<any[]>([])

const fieldLabel = computed(() => {
  const customField = integratedPartner.value?.espFeatureSupport?.customField
  if (customField && typeof customField === 'object' && 'label' in customField) {
    return customField.label
  }
  return 'Custom Field'
})

// Generate default field name when dialog opens
watch(visible, (isVisible) => {
  if (isVisible) {
    // Load existing custom fields for validation
    loadCustomFields()

    // Generate default name if we have segment group context
    if (segmentGroup && segmentGroup.value) {
      fieldName.value = `q_${toSnakeCase(segmentGroup.value.name)}`
    } else {
      fieldName.value = ''
    }

    // Clear error
    fieldError.value = ''

    // Reset auto-generate option
    autoGenerateMappings.value = true
  }
})

async function loadCustomFields() {
  try {
    customFieldsData.value = await integrationDataStore.getCustomFields()
  } catch (error) {
    console.error('Error loading custom fields:', error)
    customFieldsData.value = []
  }
}

function validateField() {
  fieldError.value = ''

  if (!fieldName.value) {
    return
  }

  // Check for invalid characters
  if (!/^[a-zA-Z0-9_]+$/.test(fieldName.value)) {
    fieldError.value = 'Only letters, numbers, and underscores are allowed'
    return
  }

  // Check for spaces
  if (fieldName.value.includes(' ')) {
    fieldError.value = 'Spaces are not allowed'
    return
  }

  // Check if field already exists
  const exists = customFieldsData.value.some(
    (field: any) =>
      String(field.id || '').toLowerCase() === fieldName.value.toLowerCase() ||
      String(field.label || '').toLowerCase() === fieldName.value.toLowerCase()
  )

  if (exists) {
    fieldError.value = `A field named "${fieldName.value}" already exists`
    return
  }
}

async function handleCreate() {
  // Final validation
  validateField()
  if (fieldError.value) {
    return
  }

  if (typeof currentTeamPid.value !== 'number') {
    fieldError.value = 'A team is required to create a custom field'
    return
  }

  isCreating.value = true

  try {
    // Create the custom field
    const response = await integrationDataStore.createCustomField(currentTeamPid.value, fieldName.value)

    if (response.data && response.data.id) {
      const newFieldId = String(response.data.id)

      // Auto-generate mappings if enabled and we have segment group context
      if (autoGenerateMappings.value && segmentGroup && segmentGroup.value) {
        try {
          // First set the field on the segment group
          segmentGroup.value.syncToCustomField = newFieldId

          // Then generate the mappings
          await generateCombinationsForSegmentGroup(segmentGroup.value, String(currentTeamPid.value))

          notificationStore.addNotification(`${fieldLabel.value} created with AI-generated mappings`)
        } catch (error) {
          console.error('Error generating mappings:', error)
          notificationStore.addError(`${fieldLabel.value} created, but could not generate mappings automatically`)
        }
      } else {
        notificationStore.addNotification(`${fieldLabel.value} "${fieldName.value}" created successfully`)
      }

      // Emit the new field ID
      emit('created', newFieldId)

      // Close dialog
      visible.value = false
    }
  } catch (error) {
    console.error('Error creating custom field:', error)
    notificationStore.addError(`Failed to create ${fieldLabel.value.toLowerCase()}`)
  } finally {
    isCreating.value = false
  }
}

function onHide() {
  // Reset form when dialog closes
  fieldName.value = ''
  fieldError.value = ''
  autoGenerateMappings.value = true
}
</script>
